/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import ReactMarkdown from 'react-markdown';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/RemoveCircleTwoTone';
import {
    bool, object, shape, string,
} from 'prop-types';
import { ChevronDown, ChevronUp } from 'lucide-react';
import transformRenderer from '../../../../../../helpers/markdown/characterTransformer';

// helpers
import { GRAPHQL_ENV } from '../../../../../../gql';
import findContent from '../../../../../../gql/queries/findContent';
import ResponsiveImage from '../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';

const useStyles = makeStyles((theme) => ({
    headingContainer: {
        backgroundColor: theme.palette.headerColor || '#E8F1ED',
        alignItems: 'center',
        textAlign: 'center',
        padding: '0.563rem 0.625rem 0.5rem',
        minHeight: '100%',
        minWidth: '206px',
        display: 'flex',
        marginRight: '1rem',
        '@media screen and (max-width: 1023.5px) and (min-width:600px)': {
            padding: '0.625rem 1rem 0.625rem 0.875rem',
            minWidth: '182px',
        },
        '@media screen and (max-width: 600px)': {
            minWidth: '182px',
            padding: '0.938rem 0.688rem',
            marginRight: '0.75rem',
        },
        [theme.breakpoints.down(380)]: {
            minWidth: '175px',
            padding: '0.938rem 0.5rem',
        },
        '@media screen and (max-width: 712px) and (min-width:601px)': {
            padding: '0.625rem',
            minWidth: '125px',
            flexDirection: 'column',
            marginRight: '0.5rem',
        },
        '& picture': {
            display: 'flex',
        },
    },
    heading: {
        fontSize: '1rem',
        '@media screen and (max-width: 712px) and (min-width:601px)': {
            lineHeight: '1.2',
        },
    },
    content: {
        fontSize: '1rem',
        alignItems: 'center',
        display: 'flex',
        width: '100%',
        '@media screen and (max-width: 1023.5px)': {
            fontSize: '0.875rem',
            display: 'block',
        },
        '& p': {
            marginRight: '0.5rem',
            '@media screen and (max-width: 1023.5px)': {
                marginBottom: '0.438rem',
                maxWidth: '100%',

            },
        },
    },
    imageContainer: {
        marginRight: '0.75rem',
        display: 'flex',
        '& img': {
            aspectRatio: '1/1',
        },
        '@media screen and (max-width: 1023.5px)': {
            marginRight: '0.5rem',
            maxHeight: '55px',
            '& img': {
                width: '55px',
            },

        },
    },
    icons: {
        paddingRight: '1rem',
        display: 'flex',
        alignItems: 'center',
        minWidth: '40px',
        justifyContent: 'flex-end',
        '& svg': {
            width: '1.5rem',
            height: '1.5rem',
        },
    },
    collapseIcon: {
        '& path:first-child': {
            fill: '#fff',
            opacity: 1,
        },
    },
    viewOption: {
        fontSize: '1rem',
        lineHeight: '1rem',
        textDecoration: 'underline',
        width: 'fit-content',
        marginRight: '0.625rem',
        fontFamily: 'LatoBold',
        textTransform: 'uppercase',
        '@media screen and (max-width: 1439.5px)': {
            fontSize: '0.875rem',
        },
        '@media screen and (max-width: 768.5px)': {
            lineHeight: '1.125rem',
        },
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: '0.438rem',
        justifyContent: 'flex-end',
        minWidth: '170px',
        '@media screen and (max-width: 1439.5px)': {
            minWidth: '155px',
        },
        '@media screen and (max-width: 1023.5px)': {
            justifyContent: 'flex-start',
        },
    },
    viewIcon: {
        display: 'flex',
        '& svg': {
            marginTop: '4px',
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                width: '20px',
                height: '20px',
                marginTop: '3px',
            },
        },
    },
    headerContainer: {
        display: 'flex',
    },
    addonHeader: {
        width: '100%',
        '& $headingContainer': {
            width: '100%',
            backgroundColor: '#ffffff',
            padding: '12px 0px 16px',
            '@media screen and (max-width: 712px) and (min-width:601px)': {
                flexDirection: 'row',
            },
        },
        '& $heading': {
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '1px',
            [theme.breakpoints.down('600')]: {
                fontSize: '14px',
                lineHeight: '18px',
            },
        },
    },
    chooseUp: {
        fontSize: '14px',
        lineHeight: '22px',
        letterSpacing: '0.5px',
        marginLeft: '12px',
        fontWeight: 'normal',
        [theme.breakpoints.down('600')]: {
            marginLeft: '8px',
        },
    },
}));

const AddonV3HeaderPanel = ({
    contentActive, brand, isPdpRedesignEnabled, addOn,
}) => {
    const classes = useStyles();
    const contentType =  'add_on_banner_v2';
    const findHeaderData = {
        brand: brand.domain,
        contentType,
        GRAPHQL_ENV,
        query: '\\"title\\" : \\"AddonV3\\"',
    };
    const ADD_ON_PAGE_QUERY = findContent(findHeaderData);
    const { loading, error, data } = useQuery(ADD_ON_PAGE_QUERY);

    if (loading) {
        return null;
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: ADD_ON_PAGE_QUERY,
            jsError: error,
            component: 'AddonV3HeaderPanel.js',
            message: `Failed to fetch content data for: content type ${contentType} on brand ${brand.domain}`,
            env: GRAPHQL_ENV,
        });
    }
    if (!data || !data.findContent?.content?.entries?.[0]) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: ADD_ON_PAGE_QUERY,
            component: 'AddonV3HeaderPanel.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }
    const { heading = '', copy = '', images = {} } = data.findContent?.content?.entries?.[0] || {};
    return (
        <div className={`${isPdpRedesignEnabled ? classes.addonHeader : classes.headerContainer} `}>
            <div className={classes.headingContainer}>
                {!isPdpRedesignEnabled && (
                    <div className={classes.imageContainer}>
                        <ResponsiveImage
                            className={`${classes.image}`}
                            path={images.url}
                            alt={images.title}
                            params={{}}
                        />
                    </div>
                )}
                <span className={classes.heading}><strong>{heading}</strong></span> {isPdpRedesignEnabled && <div className={classes.chooseUp}>(Choose up to {addOn?.maxAllowedAddons})</div>}
            </div>
            {!isPdpRedesignEnabled &&  (
                <div className={classes.content}>
                    <ReactMarkdown renderers={transformRenderer} source={copy} escapeHtml={false} />
                    <div className={classes.buttonContainer} aria-label="View Options">
                        <span className={classes.viewOption}> View Options </span>
                        <span className={classes.icons}>{(contentActive) ? <RemoveIcon className={classes.collapseIcon} /> : <AddIcon />}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
AddonV3HeaderPanel.propTypes = {
    contentActive: bool.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    isPdpRedesignEnabled: bool,
    addOn: object,
};
AddonV3HeaderPanel.defaultProps = {
    isPdpRedesignEnabled: false,
    addOn: false,
};
export default AddonV3HeaderPanel;
